import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('change', this.#handleVariantMediaChange, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    requestAnimationFrame(() => this.#promoteAdjacentMedia(0));
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Moves the selected variant image into the lead position immediately.
   * The normal section-rendering response still replaces the full gallery afterwards.
   *
   * @param {Event} event
   */
  #handleVariantMediaChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('variant-picker')) return;

    const mediaId = input.dataset.optionMediaId;
    if (!mediaId) return;

    const mediaItems = this.refs.media ?? [];
    const selectedMedia = mediaItems.find((item) => item.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`));
    if (!selectedMedia) return;

    if (this.presentation === 'grid') {
      selectedMedia.parentElement?.prepend(selectedMedia);
      return;
    }

    const index = mediaItems.indexOf(selectedMedia);
    if (index >= 0) this.slideshow?.select(index, undefined, { animate: false });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Promotes the selected product image and prewarms its immediate neighbours.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#promoteAdjacentMedia(event.detail.index);
  };

  /**
   * @param {number} index
   */
  #promoteAdjacentMedia(index) {
    const slides = this.slideshow?.refs.slides ?? [];
    if (!slides.length) return;

    for (let offset = -1; offset <= 1; offset += 1) {
      const slide = slides[index + offset];
      const image = slide?.querySelector('.product-media__image');
      if (!(image instanceof HTMLImageElement)) continue;

      image.loading = 'eager';
      image.fetchPriority = offset === 0 ? 'high' : 'auto';
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
